<template>
  <v-card flat>
    <v-card-title class="pb-1 pretrip-title">
      Driver's Vehicle Inspection Report
    </v-card-title>
    <v-card-text class="pt-4 pb-0">
      Check any defective item and give details under Remarks.
    </v-card-text>

    <v-card-text>
      <v-row dense class="mb-2">
        <v-col cols="12" sm="6">
          <div class="caption grey--text">DATE</div>
          <div class="font-weight-medium">{{ displayDate }}</div>
        </v-col>
        <v-col cols="12" sm="6">
          <div class="caption grey--text">TRUCK / TRACTOR NO.</div>
          <div class="font-weight-medium">{{ truck ? truckName : "—" }}</div>
        </v-col>
      </v-row>

      <v-divider class="mb-3"></v-divider>

      <!-- VEHICLE -->
      <div class="d-flex align-center flex-wrap mb-2">
        <h4 class="mr-3">Truck / Tractor</h4>
        <v-btn small text color="primary" @click="markAllOk('vehicle')">
          <v-icon left small>done_all</v-icon>
          Mark all OK
        </v-btn>
        <v-spacer></v-spacer>
        <span class="caption" :class="vehicleDone ? 'green--text' : 'grey--text'">
          {{ answeredCount(vehicleItems) }}/{{ vehicleItems.length }}
        </span>
      </div>

      <v-row dense>
        <v-col
          cols="12"
          md="6"
          v-for="item in vehicleItems"
          :key="item.key"
          class="py-1"
        >
          <div class="d-flex align-center item-row">
            <div class="flex-grow-1 pr-2">
              <div class="body-2">{{ item.label }}</div>
              <div v-if="item.detail" class="caption grey--text">
                {{ item.detail }}
              </div>
            </div>
            <v-btn-toggle
              :value="statuses[item.key]"
              dense
              mandatory
              @change="setStatus(item.key, $event)"
            >
              <v-btn
                small
                value="ok"
                :color="statuses[item.key] === 'ok' ? 'green' : null"
                :dark="statuses[item.key] === 'ok'"
              >
                OK
              </v-btn>
              <v-btn
                small
                value="defect"
                :color="statuses[item.key] === 'defect' ? 'red' : null"
                :dark="statuses[item.key] === 'defect'"
              >
                Defect
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <!-- TRAILER -->
      <div class="d-flex align-center flex-wrap mb-2">
        <h4 class="mr-3">Trailer(s)</h4>
        <v-btn
          small
          text
          color="primary"
          :disabled="noTrailer"
          @click="markAllOk('trailer')"
        >
          <v-icon left small>done_all</v-icon>
          Mark all OK
        </v-btn>
        <v-spacer></v-spacer>
        <v-switch
          v-model="noTrailer"
          label="No trailer today"
          hide-details
          dense
          class="mt-0 pt-0"
        ></v-switch>
      </div>

      <v-text-field
        v-if="!noTrailer"
        v-model="trailerNumber"
        label="Trailer No(s)."
        outlined
        dense
        hide-details
        class="mb-3"
      ></v-text-field>

      <v-row dense v-if="!noTrailer">
        <v-col
          cols="12"
          md="6"
          v-for="item in trailerItems"
          :key="item.key"
          class="py-1"
        >
          <div class="d-flex align-center item-row">
            <div class="flex-grow-1 pr-2">
              <div class="body-2">{{ item.label }}</div>
            </div>
            <v-btn-toggle
              :value="statuses[item.key]"
              dense
              mandatory
              @change="setStatus(item.key, $event)"
            >
              <v-btn
                small
                value="ok"
                :color="statuses[item.key] === 'ok' ? 'green' : null"
                :dark="statuses[item.key] === 'ok'"
              >
                OK
              </v-btn>
              <v-btn
                small
                value="defect"
                :color="statuses[item.key] === 'defect' ? 'red' : null"
                :dark="statuses[item.key] === 'defect'"
              >
                Defect
              </v-btn>
            </v-btn-toggle>
          </div>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <!-- REMARKS -->
      <v-alert v-if="defects.length" type="warning" text dense class="mb-3">
        <strong>{{ defects.length }}</strong>
        defective {{ defects.length === 1 ? "item" : "items" }}:
        {{ defects.map((d) => d.label).join(", ") }}.
        <br />
        Describe the problem under Remarks. The dispatcher will be notified.
      </v-alert>

      <v-textarea
        v-model="remarks"
        label="Remarks"
        :hint="
          defects.length ? 'Required when an item is marked defective.' : ''
        "
        persistent-hint
        outlined
        rows="3"
        class="mb-2"
      ></v-textarea>

      <v-checkbox
        v-model="satisfactory"
        :disabled="!!defects.length"
        hide-details
        class="mt-0 mb-4"
        label="Condition of the above vehicle is satisfactory"
      ></v-checkbox>

      <!-- SIGNATURE -->
      <h4 class="mb-2">
        Driver's Signature
        <span class="caption red--text">(REQUIRED)</span>
      </h4>
      <div v-if="!signatureData">
        <div class="signature-container mb-3">
          <VueSignature
            ref="signature"
            :w="'100%'"
            :h="'180px'"
            :sigOption="signatureOptions"
          ></VueSignature>
        </div>
        <v-btn small color="secondary" class="mr-2" @click="clearSignature">
          Clear
        </v-btn>
        <v-btn small color="primary" @click="saveSignature">
          Save Signature
        </v-btn>
      </div>
      <div v-else>
        <img
          :src="signatureData"
          style="max-width: 300px; border: 1px solid #ccc;"
        />
        <div class="caption mb-2">
          Signed by: {{ user.firstName }} {{ user.lastName }}
        </div>
        <v-btn small color="secondary" @click="clearSignature">
          Update Signature
        </v-btn>
      </div>

      <v-alert v-if="error" type="error" dense text class="mt-4 mb-0">
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        large
        :loading="submitting"
        :disabled="submitting"
        @click="submit"
      >
        Submit Inspection
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import fb from "@/main";
import utils from "@/helpers/utils";
import { mapState } from "vuex";
import moment from "moment";
import VueSignature from "vue-signature";
import { vehicleItems, trailerItems } from "@/helpers/preTripChecklist";

export default {
  components: {
    VueSignature,
  },
  props: {
    truck: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      vehicleItems,
      trailerItems,
      statuses: {},
      noTrailer: true,
      trailerNumber: "",
      remarks: "",
      satisfactory: false,
      signatureData: null,
      signatureOptions: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255,255,255)",
      },
      submitting: false,
      error: "",
    };
  },
  computed: {
    ...mapState(["user"]),
    truckName() {
      return utils.truckName(this.truck);
    },
    displayDate() {
      return moment().format("dddd, MMMM Do YYYY");
    },
    activeItems() {
      return this.noTrailer
        ? this.vehicleItems
        : [...this.vehicleItems, ...this.trailerItems];
    },
    defects() {
      return this.activeItems.filter((i) => this.statuses[i.key] === "defect");
    },
    vehicleDone() {
      return this.answeredCount(this.vehicleItems) === this.vehicleItems.length;
    },
  },
  created() {
    const statuses = {};
    [...this.vehicleItems, ...this.trailerItems].forEach((i) => {
      statuses[i.key] = null;
    });
    this.statuses = statuses;
  },
  methods: {
    answeredCount(items) {
      return items.filter((i) => this.statuses[i.key]).length;
    },
    setStatus(key, value) {
      // v-btn-toggle emits undefined when the active button is re-clicked
      this.$set(this.statuses, key, value || this.statuses[key]);
      if (this.defects.length) {
        this.satisfactory = false;
      }
    },
    markAllOk(section) {
      const items = section === "vehicle" ? this.vehicleItems : this.trailerItems;
      items.forEach((i) => this.$set(this.statuses, i.key, "ok"));
    },
    saveSignature() {
      if (this.$refs.signature) {
        if (this.$refs.signature.isEmpty()) {
          this.error = "Please provide a signature first.";
          return;
        }
        this.signatureData = this.$refs.signature.save();
        this.error = "";
      }
    },
    clearSignature() {
      if (this.$refs.signature) {
        this.$refs.signature.clear();
      }
      this.signatureData = null;
    },
    validate() {
      const unanswered = this.activeItems.filter((i) => !this.statuses[i.key]);
      if (unanswered.length) {
        this.error = `Please mark every item. ${unanswered.length} remaining (first: ${unanswered[0].label}).`;
        return false;
      }
      if (this.defects.length && !this.remarks.trim()) {
        this.error = "Remarks are required when an item is marked defective.";
        return false;
      }
      if (!this.defects.length && !this.satisfactory) {
        this.error =
          "Please confirm the condition of the vehicle is satisfactory.";
        return false;
      }
      if (!this.signatureData) {
        this.error = "A signature is required.";
        return false;
      }
      this.error = "";
      return true;
    },
    async submit() {
      if (!this.validate()) return;
      this.submitting = true;
      const date = moment().format("YYYY-MM-DD");
      const items = this.activeItems.map((i) => ({
        key: i.key,
        label: i.label,
        status: this.statuses[i.key],
      }));
      // Keyed by truck so a driver who runs two trucks in a day keeps both
      // records, and so the defect trigger still fires on the second one.
      const truckId = this.truck ? this.truck.id : "none";
      try {
        await fb.db
          .collection("preTripInspections")
          .doc(`${this.user.uid}_${date}_${truckId}`)
          .set({
            date,
            driver: {
              uid: this.user.uid,
              firstName: this.user.firstName,
              lastName: this.user.lastName,
              email: this.user.email,
              phone: this.user.phone || null,
            },
            truck: this.truck
              ? { id: this.truck.id, name: this.truckName || null }
              : null,
            truckNotApplicable: false,
            noTrailer: this.noTrailer,
            trailerNumber: this.noTrailer ? null : this.trailerNumber || null,
            items,
            defects: this.defects.map((d) => ({ key: d.key, label: d.label })),
            hasDefects: !!this.defects.length,
            remarks: this.remarks.trim() || null,
            satisfactory: this.satisfactory,
            signature: {
              signatureData: this.signatureData,
              signedAt: new Date(),
              signedBy: {
                uid: this.user.uid,
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                email: this.user.email,
              },
            },
            complete: true,
            createDate: fb.timestamp,
          });
        this.submitting = false;
        this.$emit("complete");
      } catch (error) {
        console.log("Error submitting pre-trip inspection:", error);
        this.submitting = false;
        this.error = "Error submitting inspection. Please try again.";
      }
    },
  },
};
</script>

<style scoped>
/* Vuetify sets word-break: break-all on card titles, which splits "Report" */
.pretrip-title {
  word-break: normal;
  line-height: 1.3;
}
.item-row {
  min-height: 44px;
  border-bottom: 1px solid #f0f0f0;
}
.signature-container {
  border: 1px solid #ccc;
  border-radius: 4px;
  max-width: 420px;
}
</style>
